import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const EASTERN_TZ = "America/New_York";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.0+)?Z)?$/;

export const isDateOnly = (value?: string): boolean =>
  !!value && DATE_ONLY_PATTERN.test(value.trim());

export const toEastern = (value?: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs.utc(value.trim());
  return parsed.isValid() ? parsed.tz(EASTERN_TZ) : null;
};

// Parse a picked/typed date-time as a plain wall clock. The calendar day the
// user chose must never shift with the browser's timezone, so no tz conversion.
export const parseWallClock = (value?: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const easternWallClockToIso = (wallClock: string): string | undefined => {
  const parsed = dayjs.tz(wallClock, EASTERN_TZ);
  return parsed.isValid() ? parsed.toISOString() : undefined;
};

export const formatDateToCustomFormat = (isoDate: string): string => {
  if (!isoDate) return "";

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Date(isoDate).toLocaleDateString(
    undefined,
    options
  );
  return formattedDate;
};

export const addDays = (date: Date, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + days);
  return newDate;
};

export const formattedTime = (time: string) => {
  //console.log("formattedTime", time, time.replace(/:\d{2} /, " "));
  return time?.replace(/:\d{2} /, " ") || "";
};


export const removeSecondsFromDateTime = (datetime: string): string => {
  if (!datetime) return "";
  return datetime.replace(/(\d{2}:\d{2}):\d{2}/, "$1");
};

export const differenceInDays = (end: Date, start: Date): number => {
  const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return Math.round(diff);
};

export const dateSortComparator = (v1: any, v2: any) => {
  if (v1 === "N/A" && v2 === "N/A") return 0;
  if (v1 === "N/A") return 1;
  if (v2 === "N/A") return -1;

  const parseDate = (dateStr: string) => {
    const [datePart, timePart] = dateStr.split(" ");
    const [month, day, year] = datePart.split("/");
    const [hours, minutes, seconds] = (timePart || "00:00:00").split(":");

    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10)
    ).getTime();
  };

  try {
    const date1 = parseDate(v1);
    const date2 = parseDate(v2);
    return date1 - date2;
  } catch (error) {
    console.error("date sort compare failed:", error);
    return 0;
  }
};

export const isoDateSortComparator = (v1: any, v2: any): number => {
  const isEmpty = (x: any) => x == null || x === "";
  if (isEmpty(v1) && isEmpty(v2)) return 0;
  if (isEmpty(v1)) return 1;
  if (isEmpty(v2)) return -1;

  const t1 = new Date(v1).getTime();
  const t2 = new Date(v2).getTime();
  if (Number.isNaN(t1) && Number.isNaN(t2)) return 0;
  if (Number.isNaN(t1)) return 1;
  if (Number.isNaN(t2)) return -1;
  return t1 - t2;
};
